/**
 * ========================
 * PROCUREMENTS DOMAIN MENU PROVIDER
 * ========================
 *
 * Menu configuration for the Procurements domain.
 * Sections:
 *  1. Overview — Dashboard (all users)
 *  2. Requests — My Requests, New Request (all users)
 *  3. Approvals — Pending, History (approvers/finance/admin)
 *  4. Management — All Requests, Suppliers, Purchase Orders, Invoices (admin)
 *  5. Integrations — Items, Purchase Orders, Company (admin)
 *  6. Settings — Admin, Lookups, Role Assignments (admin)
 */

import {
  IconDashboard,
  IconFileText,
  IconFilePlus,
  IconChecklist,
  IconHistory,
  IconListDetails,
  IconUsers,
  IconFileInvoice,
  IconShoppingCart,
  IconReceipt,
  IconBox,
  IconSettings,
  IconDatabase,
  IconUserShield,
  IconCash,
} from '@tabler/icons-react';
import { DomainMenuProvider, MenuSection } from '../../layouts/menu/types';
import { createVisibilityCheck } from './BaseMenuProvider';
import { useProcurementBadgesStore } from '@/domain/badges/procurementBadgesStore';

class ProcurementsMenuProvider implements DomainMenuProvider {
  domainId = 'procurements';

  getMenuConfig() {
    // Visibility constants
    const allUsers = createVisibilityCheck(); // No restriction — all staff
    const adminsOnly = createVisibilityCheck({ roles: ['Admin', 'Procurement Admin'] });

    const sections: MenuSection[] = [
      // Overview — all users
      {
        id: 'procurements-overview',
        label: 'Overview',
        items: [
          {
            id: 'procurements-dashboard',
            label: 'Dashboard',
            path: '/app/procurements',
            icon: <IconDashboard size={16} />,
            visible: allUsers,
          },
        ],
      },

      // Requests — all users
      {
        id: 'procurements-requests',
        label: 'Requests',
        items: [
          {
            id: 'procurements-my-requests',
            label: 'My Requests',
            path: '/app/procurements/requests/my-requests',
            icon: <IconFileText size={16} />,
            visible: allUsers,
            countSelector: () => useProcurementBadgesStore.getState().myRequestsInFlight || null,
            badgeColor: 'yellow',
          },
          {
            id: 'procurements-new-request',
            label: 'New Request',
            path: '/app/procurements/requests/create-request',
            icon: <IconFilePlus size={16} />,
            visible: allUsers,
          },
        ],
      },

      // Approvals — visible to all users (pages self-filter by procurement role assignment)
      // Any user assigned as an approver in /app/procurements/admin/roles will see their pending items
      {
        id: 'procurements-approvals',
        label: 'Approvals',
        items: [
          {
            id: 'procurements-pending-approvals',
            label: 'Pending',
            path: '/app/procurements/approvals/pending',
            icon: <IconChecklist size={16} />,
            visible: allUsers,
            countSelector: () => useProcurementBadgesStore.getState().pendingApprovals || null,
            badgeColor: 'red',
          },
          {
            id: 'procurements-approval-history',
            label: 'History',
            path: '/app/procurements/approvals/history',
            icon: <IconHistory size={16} />,
            visible: allUsers,
          },
        ],
      },

      // Finance — admins only
      {
        id: 'procurements-finance',
        label: 'Finance',
        visible: adminsOnly,
        items: [
          {
            id: 'procurements-finance-portal',
            label: 'Finance Portal',
            path: '/app/procurements/finance',
            icon: <IconCash size={16} />,
            visible: adminsOnly,
          },
        ],
      },

      // Management — Admin / Procurement Admin only
      {
        id: 'procurements-management',
        label: 'Management',
        visible: adminsOnly,
        items: [
          {
            id: 'procurements-all-requests',
            label: 'All Requests',
            path: '/app/procurements/requests/all-requests',
            icon: <IconListDetails size={16} />,
            visible: adminsOnly,
          },
          {
            id: 'procurements-suppliers',
            label: 'Suppliers',
            path: '/app/procurements/suppliers/list',
            icon: <IconUsers size={16} />,
            visible: adminsOnly,
          },
          {
            id: 'procurements-subscriptions',
            label: 'Subscriptions',
            path: '/app/procurements/subscriptions',
            icon: <IconReceipt size={16} />,
            visible: adminsOnly,
          },
          {
            id: 'procurements-purchase-orders',
            label: 'Purchase Orders',
            path: '/app/procurements/purchase-orders',
            icon: <IconShoppingCart size={16} />,
            visible: adminsOnly,
          },
          {
            id: 'procurements-invoices',
            label: 'Invoices',
            path: '/app/procurements/invoices',
            icon: <IconFileInvoice size={16} />,
            visible: adminsOnly,
          },
        ],
      },

      // Catalogue — all users (read-only for staff, write for admins at page level)
      {
        id: 'procurements-catalogue',
        label: 'Catalogue',
        items: [
          {
            id: 'procurements-items',
            label: 'Items',
            path: '/app/procurements/items',
            icon: <IconBox size={16} />,
            visible: allUsers,
          },
        ],
      },

      // Settings — Admin / Procurement Admin only
      {
        id: 'procurements-settings',
        label: 'Settings',
        visible: adminsOnly,
        items: [
          {
            id: 'procurements-admin',
            label: 'Admin',
            path: '/app/procurements/admin',
            icon: <IconSettings size={16} />,
            visible: adminsOnly,
          },
          {
            id: 'procurements-lookups',
            label: 'Lookups',
            path: '/app/procurements/admin/lookups',
            icon: <IconDatabase size={16} />,
            visible: adminsOnly,
          },
          {
            id: 'procurements-roles',
            label: 'Role Assignments',
            path: '/app/procurements/admin/roles',
            icon: <IconUserShield size={16} />,
            visible: adminsOnly,
          },
          {
            id: 'procurements-settings-page',
            label: 'Settings',
            path: '/app/procurements/admin/settings',
            icon: <IconSettings size={16} />,
            visible: adminsOnly,
          },
        ],
      },
    ];

    return {
      domainId: this.domainId,
      sections,
      metadata: {
        version: '2.0.0',
        lastUpdated: Date.now(),
        description: 'Procurement management menu',
      },
    };
  }
}

export const procurementsMenuProvider = new ProcurementsMenuProvider();
